import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

const profileConfigurations = {
  particulier: {
    label: "Particulier",
    icon: "👤",
    description:
      "Sélectionnez le financement qui correspond à votre besoin personnel.",
    color: "#7c3aed",
    lightColor: "#f5f3ff",
    financingTypes: [
      {
        id: "pret-personnel",
        icon: "💳",
        title: "Prêt personnel",
        description:
          "Financez un besoin personnel avec une solution adaptée à votre situation.",
      },
      {
        id: "avance-salaire",
        icon: "💼",
        title: "Avance sur salaire",
        description:
          "Faites face à une dépense urgente avant le versement de votre salaire.",
      },
      {
        id: "equipement",
        icon: "🛋️",
        title: "Achat d’équipement",
        description:
          "Financez du mobilier, de l’électroménager ou un équipement essentiel.",
      },
      {
        id: "sante",
        icon: "🏥",
        title: "Santé",
        description:
          "Financez des soins médicaux, des examens ou une intervention.",
      },
      {
        id: "logement",
        icon: "🏠",
        title: "Logement",
        description:
          "Financez un loyer, des travaux ou un besoin lié à votre habitation.",
      },
      {
        id: "mariage",
        icon: "💍",
        title: "Mariage",
        description:
          "Obtenez un accompagnement pour financer votre projet de mariage.",
      },
      {
        id: "projet-familial",
        icon: "👨‍👩‍👧‍👦",
        title: "Projet familial",
        description:
          "Financez un projet important destiné à votre famille.",
      },
      {
        id: "autre",
        icon: "✨",
        title: "Autre besoin",
        description:
          "Présentez un besoin qui ne correspond pas aux catégories proposées.",
      },
    ],
  },

  entreprise: {
    label: "Entreprise",
    icon: "🏢",
    description:
      "Sélectionnez la solution correspondant aux besoins de votre activité.",
    color: "#2563eb",
    lightColor: "#eff6ff",
    financingTypes: [
      {
        id: "tresorerie",
        icon: "💰",
        title: "Avance de trésorerie",
        description:
          "Renforcez temporairement la trésorerie de votre entreprise.",
      },
      {
        id: "salaires",
        icon: "👥",
        title: "Paiement des salaires",
        description:
          "Demandez une avance de courte durée afin de payer vos salariés.",
      },
      {
        id: "stock",
        icon: "📦",
        title: "Achat de stock",
        description:
          "Financez l’acquisition de marchandises nécessaires à votre activité.",
      },
      {
        id: "materiel",
        icon: "🖥️",
        title: "Achat de matériel",
        description:
          "Financez les équipements professionnels de votre entreprise.",
      },
      {
        id: "fournisseurs",
        icon: "🤝",
        title: "Paiement des fournisseurs",
        description:
          "Réglez vos fournisseurs sans interrompre le fonctionnement de l’activité.",
      },
      {
        id: "developpement",
        icon: "📈",
        title: "Développement",
        description:
          "Financez la croissance ou l’ouverture d’une nouvelle activité.",
      },
      {
        id: "investissement",
        icon: "🏗️",
        title: "Investissement",
        description:
          "Présentez un projet d’investissement professionnel structurant.",
      },
    ],
  },

  etudiant: {
    label: "Étudiant",
    icon: "🎓",
    description:
      "Sélectionnez le financement nécessaire à la poursuite de vos études.",
    color: "#0891b2",
    lightColor: "#ecfeff",
    financingTypes: [
      {
        id: "frais-universitaires",
        icon: "🏫",
        title: "Frais universitaires",
        description:
          "Financez vos frais d’inscription ou une partie de votre scolarité.",
      },
      {
        id: "ordinateur",
        icon: "💻",
        title: "Achat d’un ordinateur",
        description:
          "Financez un ordinateur ou du matériel nécessaire à vos études.",
      },
      {
        id: "logement-etudiant",
        icon: "🏠",
        title: "Logement étudiant",
        description:
          "Obtenez une aide pour votre caution, votre loyer ou votre installation.",
      },
      {
        id: "voyage-etudes",
        icon: "✈️",
        title: "Voyage d’études",
        description:
          "Financez un déplacement académique, un stage ou un séjour d’études.",
      },
      {
        id: "formation",
        icon: "📚",
        title: "Formation",
        description:
          "Financez une formation professionnelle ou une certification.",
      },
    ],
  },

  diaspora: {
    label: "Diaspora",
    icon: "🌍",
    description:
      "Sélectionnez le projet que vous souhaitez financer ou soutenir en Guinée.",
    color: "#059669",
    lightColor: "#ecfdf5",
    financingTypes: [
      {
        id: "investissement-guinee",
        icon: "📊",
        title: "Investissement en Guinée",
        description:
          "Financez un projet économique ou une opportunité d’investissement.",
      },
      {
        id: "aide-familiale",
        icon: "👨‍👩‍👧‍👦",
        title: "Aide familiale",
        description:
          "Financez un besoin important destiné à votre famille en Guinée.",
      },
      {
        id: "immobilier",
        icon: "🏘️",
        title: "Immobilier",
        description:
          "Financez un achat immobilier, une construction ou des travaux.",
      },
      {
        id: "commerce",
        icon: "🏪",
        title: "Commerce",
        description:
          "Financez la création ou le développement d’une activité commerciale.",
      },
      {
        id: "agriculture",
        icon: "🌱",
        title: "Agriculture",
        description:
          "Financez une exploitation agricole ou l’achat d’équipements.",
      },
      {
        id: "entrepreneuriat",
        icon: "🚀",
        title: "Entrepreneuriat",
        description:
          "Accompagnez la création d’une entreprise ou d’un nouveau projet.",
      },
    ],
  },
};

function FinanceRequest() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const profileId = searchParams.get("profil");
  const selectedType = searchParams.get("type");

  const profile = profileConfigurations[profileId];

  const handleSelectType = (typeId) => {
    setSearchParams({
      profil: profileId,
      type: typeId,
    });
  };

  const handleContinue = () => {
    if (!selectedType) {
      return;
    }

    navigate(
      `/consulting/finance/formulaire?profil=${profileId}&type=${selectedType}`
    );
  };

  if (!profile) {
    return (
      <div style={styles.page}>
        <div style={styles.errorCard}>
          <div style={styles.errorIcon}>⚠️</div>

          <h1 style={styles.errorTitle}>Profil non sélectionné</h1>

          <p style={styles.errorText}>
            Vous devez d’abord sélectionner votre profil avant de choisir un
            type de financement.
          </p>

          <button
            type="button"
            style={styles.primaryButton}
            onClick={() => navigate("/consulting/finance")}
          >
            Choisir mon profil
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <header style={styles.header}>
        <div style={styles.headerContent}>
          <button
            type="button"
            style={styles.backButton}
            onClick={() => navigate("/consulting/finance")}
          >
            ← Retour à HDC Finance
          </button>

          <div style={styles.brand}>
            <div style={styles.brandIcon}>H</div>

            <div>
              <div style={styles.brandName}>HDC Finance</div>
              <div style={styles.brandDescription}>
                Votre partenaire de financement
              </div>
            </div>
          </div>
        </div>
      </header>

      <main style={styles.main}>
        <section style={styles.introduction}>
          <div
            style={{
              ...styles.profileBadge,
              background: profile.lightColor,
              color: profile.color,
            }}
          >
            <span>{profile.icon}</span>
            Profil {profile.label}
          </div>

          <div style={styles.stepLabel}>Étape 2 sur 5</div>

          <h1 style={styles.title}>
            Quel financement recherchez-vous ?
          </h1>

          <p style={styles.subtitle}>{profile.description}</p>

          <div style={styles.progressTrack}>
            <div style={styles.progressBar} />
          </div>
        </section>

        <section style={styles.financingGrid}>
          {profile.financingTypes.map((financing) => {
            const isSelected = selectedType === financing.id;

            return (
              <button
                key={financing.id}
                type="button"
                style={{
                  ...styles.financingCard,
                  ...(isSelected
                    ? {
                        borderColor: profile.color,
                        boxShadow: `0 18px 45px ${profile.color}20`,
                      }
                    : {}),
                }}
                onClick={() => handleSelectType(financing.id)}
                aria-pressed={isSelected}
              >
                <div style={styles.cardHeader}>
                  <div
                    style={{
                      ...styles.financingIcon,
                      background: profile.lightColor,
                    }}
                  >
                    {financing.icon}
                  </div>

                  <div
                    style={{
                      ...styles.selectionIndicator,
                      ...(isSelected
                        ? {
                            background: profile.color,
                            borderColor: profile.color,
                            color: "#ffffff",
                          }
                        : {}),
                    }}
                  >
                    {isSelected ? "✓" : ""}
                  </div>
                </div>

                <h2 style={styles.financingTitle}>{financing.title}</h2>

                <p style={styles.financingDescription}>
                  {financing.description}
                </p>

                <span
                  style={{
                    ...styles.discoverLink,
                    color: profile.color,
                  }}
                >
                  {isSelected ? "Option sélectionnée" : "Sélectionner"} →
                </span>
              </button>
            );
          })}
        </section>

        <div style={styles.securityNotice}>
          <span style={styles.securityIcon}>🔒</span>

          <p style={styles.securityText}>
            Vos informations seront utilisées uniquement pour l’étude et le
            suivi de votre demande de financement.
          </p>
        </div>

        <div style={styles.bottomActions}>
          <button
            type="button"
            style={styles.secondaryButton}
            onClick={() => navigate("/consulting/finance")}
          >
            ← Modifier mon profil
          </button>

          <button
            type="button"
            disabled={!selectedType}
            onClick={handleContinue}
            style={{
              ...styles.continueButton,
              ...(!selectedType ? styles.disabledButton : {}),
            }}
          >
            Continuer vers le formulaire →
          </button>
        </div>
      </main>
    </div>
  );
}

const styles = {
  page: {
  minHeight: "100vh",
  background:
    "linear-gradient(180deg, #070b18 0px, #0b1020 540px, #f8fafc 540px, #f8fafc 100%)",
    fontFamily:
      "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },

  header: {
    borderBottom: "1px solid rgba(255, 255, 255, 0.08)",
  },

  headerContent: {
    width: "calc(100% - 48px)",
    maxWidth: "1200px",
    margin: "0 auto",
    minHeight: "82px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "24px",
    flexWrap: "wrap",
  },

  backButton: {
    padding: 0,
    border: "none",
    background: "transparent",
    color: "#cbd5e1",
    fontSize: "13px",
    fontWeight: "700",
    cursor: "pointer",
  },

  brand: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    color: "#ffffff",
  },

  brandIcon: {
    width: "40px",
    height: "40px",
    borderRadius: "13px",
    background: "linear-gradient(135deg, #7c3aed, #2563eb)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: "900",
  },

  brandName: {
    fontSize: "14px",
    fontWeight: "900",
  },

  brandDescription: {
    marginTop: "3px",
    color: "#94a3b8",
    fontSize: "10px",
  },

  main: {
    width: "calc(100% - 48px)",
    maxWidth: "1200px",
    margin: "0 auto",
    padding: "58px 0 80px",
  },

  introduction: {
    maxWidth: "760px",
    margin: "0 auto",
    textAlign: "center",
  },

  profileBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    padding: "8px 13px",
    borderRadius: "999px",
    fontSize: "12px",
    fontWeight: "900",
  },

  stepLabel: {
    marginTop: "18px",
    color: "#a78bfa",
    fontSize: "11px",
    fontWeight: "900",
    letterSpacing: "0.16em",
    textTransform: "uppercase",
  },

  title: {
    margin: "14px 0 0",
    color: "#ffffff",
    fontSize: "clamp(34px, 5vw, 52px)",
    lineHeight: 1.1,
    letterSpacing: "-0.04em",
    fontWeight: "900",
  },

  subtitle: {
    margin: "18px auto 0",
    maxWidth: "650px",
    color: "#cbd5e1",
    fontSize: "15px",
    lineHeight: 1.8,
  },

  progressTrack: {
    maxWidth: "380px",
    height: "7px",
    margin: "26px auto 0",
    borderRadius: "999px",
    background: "rgba(255, 255, 255, 0.12)",
    overflow: "hidden",
  },

  progressBar: {
    width: "40%",
    height: "100%",
    borderRadius: "999px",
    background: "linear-gradient(90deg, #7c3aed, #2563eb)",
  },

  financingGrid: {
    marginTop: "64px",
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(270px, 1fr))",
    gap: "20px",
  },

  financingCard: {
    minHeight: "270px",
    padding: "24px",
    border: "2px solid #e2e8f0",
    borderRadius: "22px",
    background: "#ffffff",
    boxShadow: "0 12px 30px rgba(15, 23, 42, 0.05)",
    textAlign: "left",
    fontFamily: "inherit",
    cursor: "pointer",
    transition: "border-color 0.2s ease, box-shadow 0.2s ease",
  },

  cardHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "16px",
  },

  financingIcon: {
    width: "54px",
    height: "54px",
    borderRadius: "17px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "24px",
  },

  selectionIndicator: {
    width: "24px",
    height: "24px",
    border: "2px solid #cbd5e1",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "transparent",
    fontSize: "13px",
    fontWeight: "900",
  },

  financingTitle: {
    margin: "22px 0 0",
    color: "#0f172a",
    fontSize: "19px",
    fontWeight: "900",
  },

  financingDescription: {
    minHeight: "72px",
    margin: "11px 0 0",
    color: "#64748b",
    fontSize: "13px",
    lineHeight: 1.7,
  },

  discoverLink: {
    display: "inline-block",
    marginTop: "18px",
    fontSize: "12px",
    fontWeight: "900",
  },

  securityNotice: {
    maxWidth: "850px",
    margin: "32px auto 0",
    padding: "15px 20px",
    border: "1px solid #e2e8f0",
    borderRadius: "15px",
    background: "#ffffff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "12px",
  },

  securityIcon: {
    fontSize: "17px",
  },

  securityText: {
    margin: 0,
    color: "#64748b",
    fontSize: "12px",
    lineHeight: 1.6,
  },

  bottomActions: {
    marginTop: "32px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "16px",
    flexWrap: "wrap",
  },

  secondaryButton: {
    padding: "14px 20px",
    border: "1px solid #cbd5e1",
    borderRadius: "13px",
    background: "#ffffff",
    color: "#334155",
    fontSize: "13px",
    fontWeight: "800",
    cursor: "pointer",
  },

  continueButton: {
    padding: "15px 22px",
    border: "none",
    borderRadius: "13px",
    background: "linear-gradient(135deg, #7c3aed, #2563eb)",
    color: "#ffffff",
    fontSize: "13px",
    fontWeight: "900",
    cursor: "pointer",
    boxShadow: "0 12px 26px rgba(37, 99, 235, 0.2)",
  },

  disabledButton: {
    background: "#cbd5e1",
    boxShadow: "none",
    cursor: "not-allowed",
  },

  errorCard: {
    width: "calc(100% - 48px)",
    maxWidth: "520px",
    margin: "0 auto",
    padding: "100px 0",
    textAlign: "center",
    color: "#ffffff",
  },

  errorIcon: {
    fontSize: "45px",
  },

  errorTitle: {
    margin: "20px 0 0",
    fontSize: "34px",
    fontWeight: "900",
  },

  errorText: {
    margin: "16px 0 28px",
    color: "#cbd5e1",
    fontSize: "14px",
    lineHeight: 1.7,
  },

  primaryButton: {
    padding: "14px 22px",
    border: "none",
    borderRadius: "13px",
    background: "linear-gradient(135deg, #7c3aed, #2563eb)",
    color: "#ffffff",
    fontSize: "13px",
    fontWeight: "900",
    cursor: "pointer",
  },
};

export default FinanceRequest;